import { DarkChapter } from "@/components/layout/DarkChapter";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

export default function TermsAndConditions() {
  return (
    <DarkChapter>
      <Navigation />
      <main className="relative z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
          {/* Overline Badge */}
          <div className="inline-block px-3 py-1 mb-6 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 border border-white/20 rounded-full">
            Legal
          </div>

          {/* Title */}
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-2">
            Terms and Conditions
          </h1>
          <p className="text-lg text-white/70 mb-1">DK Music Business Academy Ltd</p>
          <p className="text-sm text-white/50 mb-12">Last updated: 21st November 2025</p>

          {/* Introduction */}
          <div className="space-y-4 mb-12">
            <p className="text-base text-white/80 leading-relaxed">
              Welcome to www.dk-artists.com. These Terms and Conditions govern your use of our website and services. By accessing our site or engaging our services, you agree to these Terms.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                1. About Us
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  DK Music Business Academy Ltd ("we", "our", "us") is a company registered in the United Kingdom.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Registered office: Palmeira Avenue Mansions, 19 Church Road, Hove, East Sussex, BN3 2FA
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Email: <a href="mailto:hello@dk-mba.com" className="text-[#0071e3] hover:underline">hello@dk-mba.com</a>
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Website: www.dk-artists.com
                </p>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  We provide digital marketing, creative services, paid media management, consultancy, and other support for artists, creators, and brands.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                2. Definitions
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  "Client", "you", and "your" refers to any individual or business engaging our services.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  "Services" refers to any one-off project, consulting session, or retainer package delivered by us.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  "Website" refers to www.dk-artists.com.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                3. Services Offered
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We provide both one-off scoped services and ongoing retainer agreements.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  All deliverables, timelines, and fees are outlined in the client's individual proposal, invoice, or written agreement.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Any work requested outside the agreed scope will be quoted separately and is not included in your existing fee.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We may update or change our service offerings at any time.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                4. No Guarantee of Results
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We use professional expertise, proven strategies, and industry best practice. However, marketing, content performance, and music growth depend on many factors that are outside our control, including platform algorithms, audience behaviour, market conditions, and client consistency.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  For this reason:
                </p>
                <p className="text-base text-white/80 leading-relaxed font-semibold">
                  We do not guarantee any specific results, including but not limited to streams, followers, subscribers, engagement, playlist placements, revenue, fan growth, brand deals, or career success.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  All examples of past success are illustrative only and not promises of your future outcomes.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                5. Client Responsibilities
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">You agree to:</p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Provide timely access to required platforms, assets, files, and information.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Ensure all content supplied is lawful and does not infringe copyright.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Maintain the security of your accounts.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Respond to approvals, questions, and requests within reasonable timeframes.
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  If the client causes a delay of more than 14 days, we reserve the right to pause work.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Client delays do not entitle the client to refunds or extensions.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                6. Platform Access and Compliance
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  You confirm that you have lawful permission to share any data, platform access, or account credentials required for your project.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  You remain responsible for ensuring your accounts comply with platform rules and policies.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We are not liable for account restrictions, bans, or downtime caused by platform violations, changes, or technical issues.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                7. Advertising Budgets and Third-Party Behaviour
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We are not responsible for the speed or manner in which advertising platforms spend allocated budgets. Ad spend pacing, delivery, CPM/CPA fluctuations, and performance changes are controlled by the platform, not by us.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Third-party tools such as Meta, TikTok, Spotify, Google, and YouTube may alter features, algorithms, or policies at any time.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We are not responsible for delays or performance issues caused by these external systems.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                8. Payment Terms
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  Fees are outlined in the client's proposal or invoice.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  One-off projects must be paid upfront unless otherwise agreed.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Retainer fees are billed in 30-day cycles and must be paid in advance of the service period.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We may pause or terminate services if invoices remain unpaid.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  By purchasing services, you agree to resolve any billing concerns directly with us and not through chargebacks.
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                9. Refund Policy
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  Because our work involves planning, strategy, and labour from the moment a project begins:
                </p>
                <p className="text-base text-white/80 leading-relaxed font-semibold">
                  All fees for both one-off services and retainer services are strictly non-refundable.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  This applies to partial and full amounts.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  No refunds will be issued for early termination, non-participation, or changes in direction.
                </p>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                10. Retainer Cancellations
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  You may cancel a retainer by giving written notice according to the terms stated in your proposal or invoice. Unless stated otherwise, the standard notice period is 30 days.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  During the notice period:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    All scheduled payments remain due.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Services will continue until the end of the paid period.
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  Unused days, reduced activity, or delays caused by the client do not qualify for refunds or credits.
                </p>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                11. Our Right to Pause or Terminate Services
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">We may pause or terminate services if:</p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Invoices are overdue.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Platform access is removed or delayed.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Client behaviour prevents us from completing our work.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Client engages in abusive, defamatory, or inappropriate conduct.
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  No refunds will be issued if we pause or terminate services for these reasons.
                </p>
              </div>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                12. Data Processing
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  When you provide access to your accounts, fan data, email lists, or customer information, you confirm that you have the legal right to share that data with us.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We act only as a data processor on your behalf and will never use your data for purposes outside your project.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  You are responsible for compliance with UK GDPR in relation to any customer data you supply.
                </p>
              </div>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                13. Confidentiality
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  Both parties agree to keep all sensitive information confidential, including access credentials, campaign data, and unpublished material.
                </p>
              </div>
            </section>

            {/* Section 14 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                14. Intellectual Property
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  All creative assets, campaign materials, reports, strategies, templates, and documents produced by us remain our intellectual property until all invoices have been paid.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  After full payment, you receive a non-exclusive licence to use the materials for your own marketing.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We may use anonymised screenshots or project examples in our portfolio unless you request otherwise in writing.
                </p>
              </div>
            </section>

            {/* Section 15 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                15. Service Availability and Force Majeure
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We are not responsible for delays caused by events outside our control, including platform outages, API failures, illness, technical issues, strikes, or natural disasters.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  If such an event occurs, we will notify you and resume work as soon as reasonably possible.
                </p>
              </div>
            </section>

            {/* Section 16 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                16. Limitation of Liability
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  To the fullest extent permitted by UK law, DK Music Business Academy Ltd is not liable for:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Loss of profit, revenue, opportunity, or data.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Any indirect, incidental, or consequential damages.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Performance or downtime of third-party platforms.
                  </li>
                  <li className="text-white/75 relative pl-6 before:content-['•'] before:absolute before:left-0 before:text-white/50">
                    Marketing results, growth outcomes, or algorithmic changes.
                  </li>
                </ul>
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  Our total liability will not exceed the amount paid by you in the 30 days prior to the claim.
                </p>
              </div>
            </section>

            {/* Section 17 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                17. Website Use
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  You agree not to misuse our website or attempt to interfere with its function.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  We may modify or remove parts of the website at any time.
                </p>
              </div>
            </section>

            {/* Section 18 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                18. Governing Law
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  These Terms are governed by the laws of England and Wales.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Any disputes will be resolved exclusively in the courts of England and Wales.
                </p>
              </div>
            </section>

            {/* Section 19 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                19. Changes to These Terms
              </h2>
              <div className="space-y-3">
                <p className="text-base text-white/80 leading-relaxed">
                  We may update these Terms from time to time.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  The updated version becomes effective immediately once posted on this page.
                </p>
              </div>
            </section>

            {/* Section 20 */}
            <section>
              <h2 className="text-xl lg:text-2xl font-semibold text-white mb-4">
                20. Contact Us
              </h2>
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 space-y-2">
                <p className="text-base text-white/90 leading-relaxed">
                  For questions about these Terms or your services, contact us at:
                </p>
                <p className="text-base text-white/90 leading-relaxed">
                  Email: <a href="mailto:hello@dk-mba.com" className="text-[#0071e3] hover:underline">hello@dk-mba.com</a>
                </p>
                <p className="text-base text-white/90 leading-relaxed">
                  Registered office: Palmeira Avenue Mansions, 19 Church Road, Hove, East Sussex, BN3 2FA
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </DarkChapter>
  );
}
